import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from '@mui/material';
import { FaFacebook, FaInstagram, FaYoutube, FaTiktok, FaTwitter, FaPinterest, FaSnapchat, FaLinkedin } from 'react-icons/fa';
import CommonChip from '../../../common_components/CommonChip';
import HoverTooltip from '../../../common_components/hoverOverlay/HoverTooltip';
import { ShieldCheck } from "lucide-react";
const PLATFORM_ICON_MAP = {
    facebook: { Icon: FaFacebook, color: '#fff', bg: '#1877F2' },
    instagram: { Icon: FaInstagram, color: '#fff', bg: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' },
    youtube: { Icon: FaYoutube, color: '#fff', bg: '#FF0000' },
    tiktok: { Icon: FaTiktok, color: '#fff', bg: '#010101' },
    twitter: { Icon: FaTwitter, color: '#fff', bg: '#1DA1F2' },
    pinterest: { Icon: FaPinterest, color: '#fff', bg: '#E60023' },
    snapchat: { Icon: FaSnapchat, color: '#000', bg: '#FFFC00' },
    linkedin: { Icon: FaLinkedin, color: '#fff', bg: '#0A66C2' },
};
const PRIORITY_PLATFORMS = ['facebook', 'instagram', 'youtube', 'tiktok'];
const formatNumber = (val) => {
    const num = Number(val) || 0;
    if (num >= 1e6)
        return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3)
        return `${(num / 1e3).toFixed(0)}K`;
    return num.toString();
};
const sumStats = (stats) => stats ? Object.values(stats).reduce((sum, val) => sum + (Number(val) || 0), 0) : 0;
// Shown for any null/empty value across all directory columns: faded, italic "N/A".
const NA = () => (_jsx("span", { style: { fontStyle: 'italic', color: '#98A2B3', textTransform: 'none' }, children: "N/A" }));
// True when a value is null/undefined/empty, the literal "null" string from legacy data,
// or a placeholder dash ("--"/"-") the backend uses for missing values.
const isEmpty = (v) => {
    if (v === null || v === undefined)
        return true;
    if (typeof v !== 'string')
        return false;
    const t = v.trim().toLowerCase();
    return t === '' || t === 'null' || t === '--' || t === '-';
};
const formatDate = (date) => isEmpty(date) ? _jsx(NA, {}) : new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
/** Tags list tooltip */
const TagsListTooltip = ({ tags }) => (_jsx(Box, { sx: { borderRadius: '8px', overflow: 'hidden', minWidth: 130, py: 0.5 }, children: tags.map((t, i) => (_jsx(Box, { sx: {
            px: 2,
            py: 1,
            fontSize: '13px',
            fontWeight: 500,
            color: '#fff',
            textTransform: 'capitalize',
            borderBottom: i !== tags.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none',
        }, children: t }, t))) }));
/** Partners list tooltip */
const PartnersListTooltip = ({ partners }) => (_jsx(Box, { sx: { borderRadius: '8px', overflow: 'hidden', minWidth: 150, py: 0.5 }, children: partners.map((p, i) => (_jsxs(Box, { sx: {
            px: 2,
            py: 1,
            fontSize: '13px',
            fontWeight: 500,
            color: '#fff',
            textTransform: 'capitalize',
            borderBottom: i !== partners.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
        }, children: [_jsx("span", { style: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: p.partner_name || '--' }), p.partner_url && (_jsx("a", { href: p.partner_url, target: "_blank", rel: "noreferrer", style: { display: 'inline-flex', color: 'rgba(255,255,255,0.6)', transition: 'color 0.2s' }, onMouseEnter: (e) => e.currentTarget.style.color = '#fff', onMouseLeave: (e) => e.currentTarget.style.color = 'rgba(255,255,255,0.6)', children: _jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }), _jsx("path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })] }) }))] }, p._id || i))) }));
/** Partners cell component */
export const PartnersCell = ({ partners = [], onPlusClick }) => {
    const first = partners[0];
    const rest = partners.slice(1);
    // Render if there's a name OR a url (a url-only partner still shows N/A + the link icon).
    if (!first || (isEmpty(first.partner_name) && isEmpty(first.partner_url)))
        return _jsx(NA, {});
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }, children: [_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Box, { onClick: (e) => {
                            e.stopPropagation();
                            onPlusClick?.();
                        }, sx: {
                            width: 28, height: 28, borderRadius: '50%',
                            bgcolor: '#F2F4F7', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#EAECF0' }
                        }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "#667085", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19" }), _jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" })] }) }), !isEmpty(first.partner_name) && (_jsx(CommonChip, { label: first.partner_name, renderType: "chip", sx: { background: '#F2F4F7', color: '#344054', fontWeight: 500 } })), first.partner_url && (_jsx("a", { href: first.partner_url, target: "_blank", rel: "noreferrer", style: { display: 'inline-flex', color: '#98A2B3' }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }), _jsx("path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })] }) }))] }), rest.length > 0 && (_jsx(HoverTooltip, { title: _jsx(PartnersListTooltip, { partners: rest }), placement: "top", PopperProps: { sx: { '& .MuiTooltip-tooltip': { padding: 0 } } }, children: _jsx("span", { children: _jsx(CommonChip, { label: `+${rest.length}`, renderType: "tag", index: 6, sx: { cursor: 'pointer', background: '#F2F4F7', color: '#344054', fontWeight: 500 } }) }) }))] }));
};
/** Tags list component */
export const TagsCell = ({ tags = [] }) => {
    const cleaned = (tags || []).filter((t) => !isEmpty(t));
    const first = cleaned[0];
    const rest = cleaned.slice(1);
    if (!first)
        return _jsx(NA, {});
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }, children: [first && _jsx(CommonChip, { label: first, renderType: "tag", index: 0, sx: { fontSize: '14px', fontWeight: 500 } }), rest.length > 0 && (_jsx(HoverTooltip, { title: _jsx(TagsListTooltip, { tags: rest }), placement: "top", PopperProps: { sx: { '& .MuiTooltip-tooltip': { padding: 0 } } }, children: _jsx("span", { children: _jsx(CommonChip, { label: `+${rest.length}`, renderType: "tag", index: 6, sx: { cursor: 'pointer', background: '#F2F4F7', color: '#344054', fontWeight: 500 } }) }) }))] }));
};
/** Stats tooltip content */
export const StatsTooltipContent = ({ stats, moduleKey }) => {
    if (!stats || Object.keys(stats).length === 0)
        return _jsx(Box, { sx: { px: 1.75, py: 1, fontSize: 13, color: '#fff', whiteSpace: 'nowrap' }, children: "No stats" });
    const isPartner = moduleKey === 'partner_directory';
    if (isPartner) {
        const partnerMetrics = [
            { label: 'Monthly Organic Visitors', value: stats.monthly_organic_visitors, Icon: () => _jsx("span", { style: { fontSize: 14 }, children: "\uD83C\uDF43" }), bg: '#ECFDF3', color: '#027A48' },
            { label: 'Page Rank', value: stats.page_rank, Icon: () => _jsx(ShieldCheck, { size: 14, color: "#175CD3" }), bg: '#EFF8FF', color: '#175CD3' }
        ];
        return (_jsx(Box, { sx: { borderRadius: '8px', overflow: 'hidden', minWidth: 200, py: 0.5 }, children: partnerMetrics.map((m, i) => (_jsxs(Box, { sx: {
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                    borderBottom: i !== partnerMetrics.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none'
                }, children: [_jsx(Box, { sx: {
                            width: 26, height: 26, borderRadius: '50%', background: m.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }, children: _jsx(m.Icon, {}) }), _jsx("span", { style: { flex: 1, fontSize: 13, color: '#fff' }, children: m.label }), _jsx("span", { style: { fontSize: 13, fontWeight: 700, color: '#fff' }, children: m.value > 0 ? formatNumber(m.value) : '--' })] }, m.label))) }));
    }
    if (moduleKey === 'content_opportunities') {
        const rows = [];
        // SEO Metrics
        if (stats.monthly_organic_visitors > 0) {
            rows.push({ label: 'Organic Visitors', value: stats.monthly_organic_visitors, icon: _jsx("span", { style: { fontSize: 14 }, children: "\uD83C\uDF43" }), bg: '#ECFDF3', color: '#027A48' });
        }
        if (stats.authority_score > 0) {
            rows.push({ label: 'Authority Score', value: stats.authority_score, icon: _jsx(ShieldCheck, { size: 14, color: "#175CD3" }), bg: '#EFF8FF', color: '#175CD3' });
        }
        // Social Platforms
        ['facebook', 'instagram', 'youtube', 'tiktok', 'other'].forEach(p => {
            if (stats[p] > 0) {
                const map = PLATFORM_ICON_MAP[p];
                const Icon = map?.Icon;
                rows.push({
                    label: p,
                    value: stats[p],
                    icon: Icon ? _jsx(Icon, { size: 14, color: "#fff" }) : _jsx("img", { src: "/assets/otherstats.svg", style: { width: 14, height: 14, filter: 'brightness(0) invert(1)' } }),
                    bg: map?.bg || '#4B5563',
                    color: '#fff',
                    isSocial: true
                });
            }
        });
        if (rows.length === 0)
            return _jsx(Box, { sx: { px: 1.75, py: 1, fontSize: 13, color: '#fff', whiteSpace: 'nowrap' }, children: "No stats" });
        return (_jsx(Box, { sx: { borderRadius: '8px', overflow: 'hidden', minWidth: 200, py: 0.5 }, children: rows.map((r, i) => (_jsxs(Box, { sx: {
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                    borderBottom: i !== rows.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none'
                }, children: [_jsx(Box, { sx: {
                            width: 26, height: 26, borderRadius: '50%', background: r.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }, children: r.icon }), _jsx("span", { style: { flex: 1, fontSize: 13, color: '#fff', textTransform: 'capitalize' }, children: r.label }), _jsx("span", { style: { fontSize: 13, fontWeight: 700, color: '#fff' }, children: formatNumber(r.value) })] }, r.label))) }));
    }
    // Default Creator Directory logic
    const priorityEntries = [];
    const otherEntries = [];
    let othersTotal = 0;
    Object.entries(stats).forEach(([platform, value]) => {
        if (PRIORITY_PLATFORMS.includes(platform.toLowerCase())) {
            priorityEntries.push([platform, value]);
        }
        else if (typeof value === 'number') {
            otherEntries.push([platform, value]);
            othersTotal += value || 0;
        }
    });
    priorityEntries.sort((a, b) => PRIORITY_PLATFORMS.indexOf(a[0].toLowerCase()) - PRIORITY_PLATFORMS.indexOf(b[0].toLowerCase()));
    const rows = [...priorityEntries, ['Others', othersTotal]];
    return (_jsx(Box, { sx: { borderRadius: '8px', overflow: 'hidden', minWidth: 170, py: 0.5 }, children: rows.map(([platform, value], i) => {
            const key = platform.toLowerCase();
            const map = PLATFORM_ICON_MAP[key];
            const Icon = map?.Icon;
            const isOthers = platform === 'Others';
            return (_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    borderBottom: !isOthers && i !== rows.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none'
                }, children: [_jsx(Box, { sx: {
                            width: 26, height: 26, borderRadius: '50%',
                            background: map?.bg || '#4B5563',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }, children: Icon ? (_jsx(Icon, { size: 14, color: map?.color || '#fff' })) : (_jsx("img", { src: "/assets/otherstats.svg", style: { width: 14, height: 14, filter: 'brightness(0) invert(1)' } })) }), _jsx("span", { style: { flex: 1, fontSize: 13, color: isOthers ? '#94A3B8' : '#fff', textTransform: 'capitalize' }, children: platform }), _jsx("span", { style: { fontSize: 13, fontWeight: 700, color: isOthers ? '#94A3B8' : '#fff' }, children: value > 0 ? formatNumber(value) : '--' })] }, platform));
        }) }));
};
const getTypeColorData = (type) => {
    const t = type?.trim().toLowerCase();
    // Success/Positive
    if (t === 'paid' || t === 'creator' || t === 'active') {
        return { bg: '#ECFDF3', text: '#027A48' }; // Green
    }
    // Danger/Negative
    if (t === 'not paid' || t === 'notpaid' || t === 'non-paid' || t === 'nonpaid' || t === 'inactive') {
        return { bg: '#FEF3F2', text: '#B42318' }; // Rose
    }
    // Informational/Technical
    if (t === 'publisher') {
        return { bg: '#EFF8FF', text: '#175CD3' }; // Blue
    }
    if (t?.includes('platform') || t?.includes('tool')) {
        return { bg: '#F9F5FF', text: '#6941C6' }; // Purple
    }
    if (t === 'subnetwork') {
        return { bg: '#FEF6EE', text: '#B93815' }; // Orange/Burnt
    }
    if (t?.includes('coupon') || t?.includes('loyalty')) {
        return { bg: '#FFF9F5', text: '#C4320A' }; // Amber/Deep
    }
    return { bg: '#F2F4F7', text: '#364152' }; // Default Grey
};
/** Main renderCell generator */
export const getCreatorDirectoryRenderers = (row, onEdit, onSuccess, dispatch) => {
    const wrap = (node) => (_jsx(Box, { className: "all-Font-Sizes", sx: { textTransform: 'capitalize', color: '#181D27', fontSize: '14px !important' }, children: node }));
    const typeStyles = getTypeColorData(row.type || '');
    return {
        category: () => wrap(isEmpty(row.category_label) ? _jsx(NA, {}) : row.category_label.trim()),
        type: () => wrap(isEmpty(row.type_label) ? _jsx(NA, {}) : (_jsx(CommonChip, { label: row.type_label, renderType: "chip", sx: {
                background: typeStyles.bg,
                color: typeStyles.text,
                fontSize: '13px',
                fontWeight: 500,
                border: `1px solid ${typeStyles.text}20`,
            } }))),
        creator_name: () => wrap(isEmpty(row.creator_name) ? _jsx(NA, {}) : row.creator_name.trim()),
        creator_handle: () => wrap(_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 }, children: [_jsx("span", { style: { color: '#344054' }, children: isEmpty(row.creator_handle) ? _jsx(NA, {}) : row.creator_handle }), row.page_link && (_jsx("a", { href: row.page_link, target: "_blank", rel: "noreferrer", style: { display: 'inline-flex', color: '#98A2B3' }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }), _jsx("path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })] }) }))] })),
        stats: () => {
            const total = Number(row.total_stats || sumStats(row.stats)) || 0;
            return wrap(total > 0 ? (_jsx(HoverTooltip, { title: _jsx(StatsTooltipContent, { stats: row.stats, moduleKey: row.module_key }), placement: "top", PopperProps: { sx: { '& .MuiTooltip-tooltip': { padding: 0 } } }, children: _jsx("span", { children: _jsx(CommonChip, { label: formatNumber(total), renderType: "chip", sx: { background: '#D1FADF', color: '#067647', cursor: 'default', fontWeight: 500 } }) }) })) : _jsx(NA, {}));
        },
        contact_name: () => wrap(isEmpty(row.contact_name) ? _jsx(NA, {}) : row.contact_name),
        contact_title: () => wrap(isEmpty(row.contact_title) ? _jsx(NA, {}) : row.contact_title),
        tags: () => wrap(_jsx(TagsCell, { tags: row.tags })),
        primary_lead_name: () => wrap(isEmpty(row.primary_lead_name) ? _jsx(NA, {}) : (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(CommonChip, { label: row.primary_lead_name, renderType: "tag", index: 4, sx: { cursor: 'default', fontSize: '14px', fontWeight: 500 } }), row.primary_lead_email && (_jsx("a", { href: `https://mail.google.com/mail/u/0/?fs=1&to=${row.primary_lead_email}&tf=cm`, target: "_blank", rel: "noreferrer", style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#F2F4F7',
                        color: '#667085',
                        transition: 'all 0.2s',
                        textDecoration: 'none'
                    }, onMouseEnter: (e) => {
                        e.currentTarget.style.backgroundColor = '#EAECF0';
                        e.currentTarget.style.color = '#344054';
                    }, onMouseLeave: (e) => {
                        e.currentTarget.style.backgroundColor = '#F2F4F7';
                        e.currentTarget.style.color = '#667085';
                    }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "22", y1: "2", x2: "11", y2: "13" }), _jsx("polygon", { points: "22 2 15 22 11 13 2 9 22 2" })] }) }))] }))),
        partners: () => wrap(_jsx(PartnersCell, { partners: row.partners, onPlusClick: () => onEdit({ ...row, open_partner_tab: true }) })),
        publisher: () => wrap(_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 250 }, children: [_jsx(HoverTooltip, { title: row.publisher || '', placement: "top", children: _jsx("span", { style: {
                            color: '#181D27',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'inline-block',
                            maxWidth: 210
                        }, children: isEmpty(row.publisher) ? _jsx(NA, {}) : row.publisher }) }), row.website && (_jsx("a", { href: row.website.startsWith('http') ? row.website : `https://${row.website}`, target: "_blank", rel: "noreferrer", style: { display: 'inline-flex', color: '#98A2B3' }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }), _jsx("path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })] }) }))] })),
        requirement: () => wrap(_jsx(Box, { sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }, children: isEmpty(row.requirement) ? (isEmpty(row.notes) ? _jsx(NA, {}) : row.notes) : row.requirement })),
        contentType: () => {
            const isPaid = row.contentType?.toLowerCase() === 'paid';
            return wrap(_jsx(CommonChip, { label: row.contentType || 'Non-Paid', sx: {
                    backgroundColor: isPaid ? '#D1FADF' : '#F2F4F7',
                    color: isPaid ? '#027A48' : '#344054',
                } }));
        },
        date_added: () => wrap(formatDate(row.date_added || row.createdAt)),
        createdAt: () => wrap(formatDate(row.createdAt || row.date_added)),
    };
};
